<script>
import { VDataTable } from 'vuetify/lib';
import BasePhoneTable from '@/components/base/BasePhoneTable.vue';
import { viewport } from '@/util/viewport';

const firstOf = (value) => (Array.isArray(value) ? value[0] : value);

export default {
  name: 'TableNormal',
  extends: VDataTable,
  props: {
    hasShadow: {
      type: Boolean,
      default: true,
    },
    // Phones, for a table the server pages: more rows to load at the bottom of the list
    // (`load-more`), where the larger screens show page numbers.
    hasMore: {
      type: Boolean,
      default: false,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
    loadMoreError: {
      type: [Object, Error, String],
      default: null,
    },
    // A new search is running: asking for more now would cancel it.
    pending: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // The order picked in the phone list's sort sheet; until then, the table's own.
      phoneSort: null,
    };
  },
  computed: {
    // Phones list the rows as the phone lists do (BasePhoneTable), described by the headers'
    // `phone` parts. Rows that select, expand or that the page lays out itself keep Vuetify's cards.
    showsPhoneList() {
      const ownRows = this.$scopedSlots.item || this.$scopedSlots.body || this.$scopedSlots['expanded-item'];
      return viewport.size === 'phone' && !this.showSelect && !this.showExpand && !ownRows;
    },
    phoneSortState() {
      if (this.phoneSort) return this.phoneSort;
      const options = this.options || {};
      const by = firstOf(options.sortBy) || firstOf(this.sortBy);
      const desc = firstOf(options.sortDesc) || firstOf(this.sortDesc);
      return by ? { by, desc: !!desc } : null;
    },
    // Filtered and sorted as the table does it, with the page's own filter and sorters.
    phoneItems() {
      const items = this.search ? this.customFilterWithColumns(this.items, this.search) : this.items;
      const sort = this.phoneSortState;
      if (!sort || this.disableSort) return items;
      return this.customSortWithHeaders(items.slice(), [sort.by], [sort.desc], this.$vuetify.lang.current);
    },
  },
  methods: {
    updateStyles() {
      // Dynamically set CSS variables based on props
      this.$el.style.setProperty('--box-shadow', this.hasShadow ? ' 0 25px 37px 0 rgba(#bdc9d6, 0.24)' : 'none');
    },
    // Told as the table tells it, for the pages that sort on the server.
    onPhoneSort(sort) {
      this.phoneSort = sort;
      this.$emit('update:sort-by', Array.isArray(this.sortBy) ? [sort.by] : sort.by);
      this.$emit('update:sort-desc', Array.isArray(this.sortDesc) ? [sort.desc] : sort.desc);
    },
  },
  render(h) {
    if (!this.showsPhoneList) return VDataTable.options.render.call(this, h);

    const sort = this.phoneSortState;
    return h(BasePhoneTable, {
      props: {
        headers: this.headers,
        items: this.phoneItems,
        itemKey: this.itemKey,
        loading: !!this.loading,
        noDataText: this.$vuetify.lang.t(this.loading ? this.loadingText : this.noDataText),
        sortable: !this.disableSort,
        sortKey: sort ? `${sort.by}:${sort.desc ? 'desc' : 'asc'}` : '',
        clickable: !!this.$listeners['click:row'],
        loadsMore: !!this.$listeners['load-more'],
        hasMore: this.hasMore,
        loadingMore: this.loadingMore,
        loadMoreError: this.loadMoreError,
        pending: this.pending,
      },
      on: {
        'click:row': (item) => this.$emit('click:row', item),
        'load-more': () => this.$emit('load-more'),
        sort: this.onPhoneSort,
      },
      scopedSlots: this.$scopedSlots,
    });
  },
  mounted() {
    this.updateStyles();
  },
  watch: {
    hasShadow() {
      this.updateStyles();
    },
  },
};
</script>
<style lang="scss">
// Basic table
// Use 'dense' property for dense style
// Specify mobile breakpoint using
// :mobile-breakpoint="1000" (Any breakpoint is ok, styles are based on injected classes)

.v-data-table.theme--light {
  border-radius: $border-radius-root !important;
  padding: 12px 0 0;

  &.v-data-table--fixed-header {
    .v-data-table__wrapper {
      overflow-x: initial;
      overflow-y: initial;
    }

    thead th {
      // bottom border when scrolling and header is fixed
      position: relative;
      box-shadow: $shadow-data-table-inset;

      span {
        align-items: center;
      }
    }
  }

  table {
    border-collapse: separate;
    position: relative;
    z-index: $zero-index;
    box-shadow: $shadow-default;
    border-radius: $border-radius-root !important;
  }

  .v-data-table-header tr th {
    border-bottom: none;
    color: $color-neutral;
    font-size: 16px;
    font-weight: normal;
    padding: 12px 16px;
    vertical-align: middle;
    white-space: nowrap;

    span {
      display: inline-flex;
      flex: auto;
      white-space: normal;
    }

    &.sortable:hover {
      color: $color-normal;
    }

    &.sortable.active {
      color: $color-normal;
    }

    &:first-child {
      padding-left: 16px;
      z-index: $third-index;
    }
  }

  .mdi-arrow-up {
    &::before {
      color: $color-accent;
      content: '\f143';
      font-size: 16px;
    }
  }

  tbody {
    cursor: pointer;

    tr {
      transition: background-color 200ms ease, box-shadow 200ms ease, border-top-color 200ms ease;
      position: relative;

      &:hover,
      &.highlight {
        box-shadow: $shadow-lg;
        z-index: $second-index;
      }

      &:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper),
      &.v-data-table__selected {
        background: transparent;
      }

      &:first-child {
        td {
          border-top: none;
        }
      }

      &.highlight {
        background-color: $color-accent-100;
      }

      &:not(:last-child) td:last-child,
      &:not(:last-child) td:not(.v-data-table__mobile-row) {
        border-bottom: none;
      }
    }

    td {
      background-color: transparent;
      border-bottom: none;
      border-top: 1px solid $color-border;
      padding: 12px 16px !important;
      color: $color-normal-pale;
      height: auto;

      &:first-child {
        padding-left: 16px !important;
      }
    }
  }

  // Mobile presentation
  .v-data-table-header-mobile tr th {
    padding: 8px 16px !important;
  }

  .v-data-table-header-mobile__select {
    min-width: auto;
    padding-right: 12px;
    z-index: $base-index;
  }

  .v-data-table__mobile-row {
    border-top: none;
    padding: 8px 24px !important;

    &__wrapper {
      justify-content: flex-start;
      width: 100%;
      display: block;
    }

    &__cell {
      display: flex;
      justify-content: flex-start;
      text-align: left;
      text-overflow: ellipsis;
      width: 100%;
    }

    &__header {
      width: 100%;
      color: $color-neutral;
      padding-bottom: 4px;
      font-weight: normal;
    }

    &:first-child {
      border-top: 1px solid $color-border;
      padding-top: 32px;
      padding-left: 32px;
    }

    &:last-child {
      padding-bottom: 32px;
    }
  }

  .v-data-table__mobile-row__header {
    padding-right: 16px;
  }
  .theme--light.v-chip:not(.v-chip--active) {
    flex: initial;

    .v-icon {
      opacity: 1;
    }
  }

  // Dense presentation
  &.v-data-table--dense {
    .v-data-table-header tr th {
      font-size: 12px;
      padding: 24px 28px 24px 0;

      &:first-child {
        z-index: $third-index;
      }
    }
    tbody {
      td {
        padding: 24px 28px 24px 0;

        &:first-child {
          padding-left: 16px;
        }
      }
    }

    // Dense mobile presentation
    .v-data-table__mobile-row {
      padding: 8px 28px;

      &:first-child {
        padding-top: 24px;
      }

      &:last-child {
        padding-bottom: 24px;
      }
    }
  }

  // Basic helper classes (scoped to table)
  .text-left,
  .text-start {
    text-align: left;
  }

  .text-right {
    text-align: right;
  }

  .text-center {
    text-align: center;
  }

  .orange {
    color: $color-orange;
  }

  .red {
    color: $color-error;
  }

  .normal {
    color: $color-green;
  }

  .is-read {
    color: $color-normal-pale;
    font-weight: normal;
  }

  .is-unread {
    color: $color-normal;
    font-weight: bold;
  }

  .is-read-date {
    color: $color-normal-pale;
    font-weight: normal;

    &.orange {
      color: $color-orange;
    }
    &.red {
      color: $color-error;
    }
    &.normal {
      color: $color-green;
    }
  }

  .is-unread-date {
    color: $color-normal;
    font-weight: bold;
  }
}
</style>
